<template>
	<!-- split mobile app and "normal" app behaviour even though they might be combined -->
	<!-- this makes sense to make changes in the future simpler and the behaviour more explicit -->
	<nuxt-link
		v-if="isCordova()"
		:id="id"
		:to="computedLink"
		:target="isExternalLink ? '_blank' : target"
		:name="name"
		:rel="computedRel"
		:class="linkClasses"
		:aria-label="ariaLabel"
		v-bind="$attrs"
		@click="startInAppBrowser($event)"
	>
		<slot />
	</nuxt-link>
	<span
		v-else-if="invisible && to"
		:id="id"
		:rel="computedRel"
		:class="linkClasses"
		class="cursor-pointer"
		:aria-label="ariaLabel"
		v-bind="$attrs"
		role="link"
		tabindex="0"
		@click="openInvisibleLink"
		@keydown.enter="openInvisibleLink"
	>
		<slot />
	</span>
	<!-- keep implicit target behaviour for now to avoid breaking changes -->
	<nuxt-link
		v-else
		:id="id"
		:to="to"
		:target="isExternalLink ? '_blank' : target"
		:name="name"
		:rel="computedRel"
		:class="linkClasses"
		:aria-label="ariaLabel"
		v-bind="$attrs"
	>
		<slot />
	</nuxt-link>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { IAPILoginUser } from '@hokify/common';
import { externalLinkHelper } from '../helpers/externalLink';
import { nativeLinkOpener } from '../helpers/nativeLinkOpener';

export default defineComponent({
	name: 'HokLink',
	data() {
		const { cordova, env } = useRuntimeConfig().public;

		return {
			env,
			cordova
		};
	},
	computed: {
		topLevelDomain(): string | undefined {
			return this.$nuxt?.$userRootStore?.topLevelDomain;
		},
		user(): IAPILoginUser | undefined {
			return this.$nuxt?.$userProfileStore?.obj;
		},
		domainRegion(): string {
			const validRegions = ['at', 'de'];

			// 1st: use top level domain from store
			if (this.topLevelDomain && validRegions.includes(this.topLevelDomain)) {
				return this.topLevelDomain;
			}

			// 2nd: use region from user obj
			const userObj = this.user;
			if (userObj) {
				const countryCode =
					userObj.general?.address?.countryCode?.toLowerCase() ||
					userObj.internal?.region?.toLowerCase();

				if (validRegions.includes(countryCode)) {
					return countryCode;
				}
			}

			// 3rd: fallback to .com
			return 'com';
		},
		isExternalLink(): boolean {
			if (this.linkType === 'external') {
				return true;
			}
			if (this.linkType === 'internal') {
				return false;
			}

			if (import.meta.server) {
				return false;
			}

			const matchedRoute = this.$router.resolve(this.to);

			// avoid matching /company as a jobNr
			return !matchedRoute.matched?.length || matchedRoute.params?.jobNr === 'company';
		},
		externalLink(): string | undefined {
			const target = this.cordova ? '_blank' : this.target;
			return externalLinkHelper(this.env, this.to, target, this.domainRegion);
		},
		linkClasses(): string[] {
			switch (this.styling) {
				case 'unstyled':
					return [];
				case 'typeform':
					return ['inline'];
				case 'inline-block':
					return ['inline-block'];
				default:
					return ['inline-flex'];
			}
		},
		computedRel(): string {
			if (this.isExternalLink) {
				// TODO remove after adjust removal with proper deeplinking
				// https://app.asana.com/1/60604373080989/project/1207203365740563/task/1209948149357431?focus=true
				const isAdjustLink = this.to.startsWith('https://67ra.adj.st');
				if (isAdjustLink) {
					return 'noopener noreferrer nofollow';
				}
				return 'noopener noreferrer';
			}
			return '';
		},
		computedLink(): string {
			if (this.isExternalLink) {
				return this.externalLink ?? this.to;
			}
			return this.to;
		}
	},
	methods: {
		isCordova(): boolean {
			return !!(
				(import.meta.client && window.location.protocol === 'file:') ||
				useRuntimeConfig().public.cordova
			);
		},
		async startInAppBrowser($event: Event) {
			if (!this.isExternalLink) {
				return;
			}

			// only on cordova
			if (this.isCordova()) {
				await nativeLinkOpener(
					this.computedLink,
					'_blank',
					$event,
					'location=yes',
					this.$nuxt?.$capacitorPlugins?.Browser
				);
			}
		},
		async openInvisibleLink() {
			const options = {
				external: this.isExternalLink,
				open: {
					target: this.target,
					windowFeatures: {} as { noopener?: boolean; noreferrer?: boolean }
				}
			};

			if (options.external) {
				options.open.windowFeatures.noopener = true;
				options.open.windowFeatures.noreferrer = true;
			}

			return navigateTo(this.computedLink, options);
		}
	},
	props: {
		to: { type: String, required: true },
		id: { type: String },
		name: { type: String },
		linkType: { type: String, default: 'internal' },
		target: { type: String, default: '_self' },
		ariaLabel: { type: String },
		styling: {
			type: String,
			default: undefined,
			validator: (value: string) => ['unstyled', 'inline-block', 'typeform'].includes(value)
		},
		invisible: { type: Boolean, default: false }
	}
});
</script>
